import axios, { type AxiosRequestConfig } from 'axios';
import type { BoundingBox } from '@/types/BoundingBox.types';
import type { Product, ProductReviewData } from '@/types/Product.types';
import slaydApi from './axiosInstance';

export interface HomePageProductListData {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  hasNext: boolean;
  hasPrevious: boolean;
  results: Product[];
}

export interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
}

export interface SimilarProductsAPIResponse {
  products: Product[];
  pagination: PaginationInfo;
  quickFilters?: string[]; // Added quickFilters
}

export interface SimilarProductsParams {
  page?: number;
  itemsPerPage?: number;
  minPrice?: number;
  maxPrice?: number;
}

export interface SimilarProductsImageParams extends SimilarProductsParams {
  imageUrl?: string;
  productId?: number;
  searchType?: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  sale?: boolean;
  brands?: string[];
  marketplaces?: string[];
}

export interface SimillarProductsTextParams extends SimilarProductsParams {
  text: string;
  sale?: boolean;
}

export const fetchSimilarProducts = async (
  params: SimilarProductsImageParams,
  config?: AxiosRequestConfig
): Promise<SimilarProductsAPIResponse> => {
  try {
    const queryParams: Record<string, any> = {
      search_type: params.searchType || 'combined_75',
      page: params.page || 1,
      items_per_page: params.itemsPerPage || 10,
      min_price: params.minPrice,
      max_price: params.maxPrice,
      ...(params.sale ? { sale: true } : {}),
    };

    if (params.productId) {
      queryParams.product_id = params.productId;
    }
    if (params.imageUrl) {
      queryParams.image_url = params.imageUrl;
    }
    if (params.x !== undefined) queryParams.x = params.x;
    if (params.y !== undefined) queryParams.y = params.y;
    if (params.width !== undefined) queryParams.width = params.width;
    if (params.height !== undefined) queryParams.height = params.height;
    if (params.brands && params.brands.length) {
      queryParams.brands = params.brands;
    }
    if (params.marketplaces && params.marketplaces.length) {
      queryParams.marketplaces = params.marketplaces;
    }

    const response = await slaydApi.get<SimilarProductsAPIResponse>(
      `/api/similar-products/`,
      {
        params: queryParams,
        ...config,
      }
    );

    if (!response?.data?.products) {
      throw new Error('Invalid Response');
    }

    return response.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      throw error;
    }
    console.error(
      `Error fetching similar products for ${params.imageUrl}: \n`,
      error
    );
    throw new Error(`Failed fetching similar products`);
  }
};

export const fetchSimilarProductsWithBox = async ({
  imageUrl,
  pageNum = 1,
  box,
  minPrice,
  maxPrice,
  sale,
  config,
}: {
  imageUrl: string;
  pageNum?: number;
  box?: BoundingBox;
  minPrice?: number;
  maxPrice?: number;
  sale?: boolean;
  config?: AxiosRequestConfig;
}): Promise<Product[]> => {
  const response = await fetchSimilarProducts(
    {
      imageUrl: imageUrl,
      page: pageNum,
      x: box?.x,
      y: box?.y,
      width: box?.width,
      height: box?.height,
      minPrice: minPrice,
      maxPrice: maxPrice,
      sale: sale,
    },
    config
  );

  return response.products;
};

/**
 * Fetch a product's data by its ID.
 * The API returns an array, so we return the first item.
 */
export async function fetchProductDataById(id: string): Promise<Product> {
  try {
    const response = await slaydApi.get<Product[]>('/api/myntra/products/', {
      params: { id },
    });

    if (!response.data || !response.data.length)
      throw new Error('Invalid response');

    return response.data[0];
  } catch (error) {
    console.error(error);
    throw new Error(`Error fetching product data`);
  }
}

export const fetchSimilarProductsByText = async (
  params: SimillarProductsTextParams,
  config?: AxiosRequestConfig
): Promise<SimilarProductsAPIResponse> => {
  try {
    const response = await slaydApi.get<SimilarProductsAPIResponse>(
      `/api/text-search/`,
      {
        params: {
          text: params.text,
          min_price: params.minPrice,
          max_price: params.maxPrice,
          page: params.page || 1,
          ...(params.page === 1 ? { get_quick_filters: true } : {}), // Only add when page=1
          ...(params.sale ? { sale: true } : {}),
        },
        ...config,
      }
    );

    if (!response?.data?.products) {
      throw new Error('Invalid Response');
    }

    return response.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      throw error;
    }
    console.error(
      `Error fetching similar products for ${params.text}: \n`,
      error
    );
    throw new Error(`Failed fetching similar products`);
  }
};

export const getHomePageInfiniteProducts = async (
  pageNum: number
): Promise<HomePageProductListData> => {
  try {
    const response = await slaydApi.get<HomePageProductListData>(
      `/api/homepage/`,
      {
        params: {
          page: pageNum,
        },
      }
    );

    if (!response?.data?.results) {
      throw new Error('Invalid Response');
    }

    return response.data;
  } catch (error) {
    console.error(`Error fetching products list for homepage: \n`, error);
    throw new Error(`Failed fetching products list for homepage`);
  }
};

export interface GenerateSimilarProductsFeedParams {
  imageUrls: string[];
  minPrice?: number;
  maxPrice?: number;
  sale?: boolean;
  brands?: string[];
  marketplaces?: string[];
  page?: number;
}

export interface GenerateSimilarProductsFeedResponse {
  products: Product[];
  message?: string;
  pagination?: PaginationInfo;
}

export interface InternalCurationFilterOption {
  id: string;
  label: string;
  value: string;
}

interface FiltersFacetOption {
  id: string;
  label: string;
  value?: string;
  type?: string;
}

interface FiltersFacet {
  id: string;
  label: string;
  type: string;
  param: string;
  options?: FiltersFacetOption[];
}



/**
 * Generate a product feed from multiple uploaded image URLs
 */
export const generateSimilarProductsFeed = async (
  params: GenerateSimilarProductsFeedParams
): Promise<GenerateSimilarProductsFeedResponse> => {
  try {
    const requestBody: Record<string, unknown> = {
      image_urls: params.imageUrls,
    };

    if (params.minPrice !== undefined) requestBody.min_price = params.minPrice;
    if (params.maxPrice !== undefined) requestBody.max_price = params.maxPrice;
    if (params.sale !== undefined) requestBody.sale = params.sale;
    if (params.brands?.length) requestBody.brands = params.brands;
    if (params.marketplaces?.length)
      requestBody.marketplaces = params.marketplaces;
    if (params.page !== undefined) requestBody.page = params.page;

    const response = await slaydApi.post<GenerateSimilarProductsFeedResponse>(
      '/api/similar-products/generate-feed/',
      requestBody
    );

    if (!response?.data?.products) {
      throw new Error('Invalid Response');
    }

    return response.data;
  } catch (error) {
    console.error('Error generating similar products feed:', error);
    throw new Error('Failed to generate similar products feed');
  }
};

export interface ProductDataForPost {
  product_link: string;
  name?: string;
  price?: string;
  discount_price?: string;
  brand?: string;
  color?: string;
  category?: string;
  subcategory?: string;
  gender?: string;
  marketplace?: string;
  image_url?: string;
  secondary_images?: string;
  available_sizes?: string;
  description?: string;
  [key: string]: string | undefined;
}

export const fetchInternalCurationFilters = async (): Promise<{
  brands?: {
    id: string;
    label: string;
    type: string;
    param: string;
    searchable: boolean;
    options: InternalCurationFilterOption[];
  };
  marketplaces?: {
    id: string;
    label: string;
    type: string;
    param: string;
    searchable: boolean;
    options: InternalCurationFilterOption[];
  };
}> => {
  try {
    const response = await slaydApi.get<any>(
      '/api/filters/',
      { params: { context: 'category' } }
    );
    const facets: FiltersFacet[] =
      response.data?.facets ||
      response.data?.category?.facets ||
      [];

    const allFacetOptions = [
      ...(facets.find((f) => f.id === 'brand' || f.param === 'brand')?.options || []),
      ...(facets.find((f) => f.id === 'marketplace' || f.param === 'marketplace')?.options || []),
    ];

    const brandOpts = allFacetOptions.filter(
      (opt) => !opt.type || opt.type.toLowerCase() === 'brand'
    );
    const mpOpts = allFacetOptions.filter(
      (opt) => opt.type && opt.type.toLowerCase() === 'marketplace'
    );

    const brands = brandOpts.map((option) => ({
      id: String(option.id || option.label),
      label: String(option.label || option.id),
      value: String(option.label || option.id || option.value),
    }));

    const marketplaces = mpOpts.map((option) => ({
      id: String(option.id || option.label),
      label: String(option.label || option.id),
      value: String(option.label || option.id || option.value),
    }));

    return {
      brands: {
        id: 'brands',
        label: 'Brands',
        type: 'multi_select',
        param: 'brands',
        searchable: true,
        options: brands,
      },
      marketplaces: {
        id: 'marketplaces',
        label: 'Marketplaces',
        type: 'multi_select',
        param: 'marketplaces',
        searchable: false,
        options: marketplaces,
      },
    };
  } catch (error) {
    console.error('Error fetching internal curation filters:', error);
    return {};
  }
};

export async function createProduct(
  productData: ProductDataForPost
): Promise<Product> {
  try {
    const { product_link, ...otherFields } = productData;

    if (!product_link) {
      throw new Error('product_link is required');
    }

    const requestBody: Record<string, unknown> = {
      product_link: product_link,
    };

    Object.entries(otherFields).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        requestBody[key] = value;
      }
    });

    const response = await slaydApi.post<Product | Product[]>(
      '/api/myntra/products/',
      requestBody
    );

    const product = Array.isArray(response.data)
      ? response.data[0]
      : response.data;

    if (!product) {
      throw new Error('Invalid response');
    }

    return product;
  } catch (error) {
    console.error('Error creating product from link:', error);
    throw new Error('Failed to create product from link');
  }
}

/**
 * Fetch additional info like reviews for a product
 */
export async function fetchProductAdditionalInfo(params: {
  id: string;
}): Promise<ProductReviewData> {
  try {
    const response = await slaydApi.get<ProductReviewData>(
      '/api/myntra/products/additional-info/',
      {
        params,
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching additional product info:', error);
    throw new Error('Failed to fetch additional product info');
  }
}
